import React, { useState, useEffect, useCallback } from 'react';
import {
    Loader2,
    AlertCircle,
    RefreshCw,
    Filter,
    Trophy,
    Award,
    TrendingUp,
    Target,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    User,
    BookOpen,
    GraduationCap,
    BarChart3,
    X,
    Eye,
    Calendar,
    Check,
} from "lucide-react";
import { ApiService } from "../../../../services/api";
import DepartmentTreeSelect from "../selectors/DepartmentTreeSelect";
import Modal from "../../../../components/ui/Modal";

interface UserScore {
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email?: string;
        schoolNumber?: string;
        department?: {
            id: string;
            name: string;
            color: string;
        };
        avatar?: string | null;
    };
    successRate: number;
    totalHomeworks: number;
    completedHomeworks: number;
    completionRate: number;
    lessonScores: LessonScore[];
    rank?: number;
}

interface LessonScore {
    id: string;
    title: string;
    successRate: number;
    totalHomeworks: number;
    completedHomeworks: number;
    completionRate: number;
    topics?: TopicScore[];
}

interface TopicScore {
    id: string;
    title: string;
    successRate: number;
    totalHomeworks: number;
    completedHomeworks: number;
    completionRate: number;
}

interface SuccessScoreReportData {
    data: UserScore[];
    meta: {
        totalItems: number;
        totalPages: number;
        currentPage: number;
        perPage: number;
    };
    departmentScore: number;
    lessonScores: LessonScore[];
    statistics: {
        totalStudents: number;
        totalHomeworks: number;
        completedHomeworks: number;
        overallCompletionRate: number;
    };
}

interface UserDetailData {
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email?: string;
        schoolNumber?: string;
        department?: any;
        avatar?: string | null;
    };
    successRate: number;
    totalHomeworks: number;
    completedHomeworks: number;
    completionRate: number;
    lessonScores: Array<{
        id: string;
        title: string;
        successRate: number;
        totalHomeworks: number;
        completedHomeworks: number;
        completionRate: number;
        topics: TopicScore[];
    }>;
}

interface SuccessScoreReportTabProps {
    mode?: "instant" | "monthly";
}

export default function SuccessScoreReportTab({ mode = "instant" }: SuccessScoreReportTabProps) {
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
    const [reportData, setReportData] = useState<SuccessScoreReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filtered, setFiltered] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);

    const [availablePeriods, setAvailablePeriods] = useState<Array<{ period: string; year: number; month: number; name: string }>>([]);
    const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
    const [loadingPeriods, setLoadingPeriods] = useState(false);
    const [monthlyReportData, setMonthlyReportData] = useState<any>(null);
    const [monthlyLoading, setMonthlyLoading] = useState(false);
    const [monthlyFiltered, setMonthlyFiltered] = useState(false);

    const [studentDepartmentId, setStudentDepartmentId] = useState<string | null>(null);
    const [settingsLoading, setSettingsLoading] = useState(true);

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState<UserDetailData | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);

    const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());

    useEffect(() => {
        const loadSettings = async () => {
            try {
                setSettingsLoading(true);
                const settings = await ApiService.getSettings();
                const educationSettings = settings?.education || settings;
                if (educationSettings?.studentDepartmentId) {
                    setStudentDepartmentId(educationSettings.studentDepartmentId);
                }
            } catch (err) {
                console.error("Error loading settings:", err);
            } finally {
                setSettingsLoading(false);
            }
        };
        loadSettings();
    }, []);

    const loadReport = useCallback(
        async (currentPage: number = 1, isPagination: boolean = false) => {
            if (!selectedDepartments || selectedDepartments.length === 0) {
                return;
            }

            if (isPagination) {
                setIsTableLoading(true);
            } else {
                setLoading(true);
            }
            setError(null);

            try {
                const response = await ApiService.getSuccessScoreReport(
                    selectedDepartments[0],
                    currentPage,
                    limit
                );
                setReportData(response);
                setPage(currentPage);
            } catch (err: any) {
                console.error("Error loading success score report:", err);
                setError(err?.message || "Rapor yüklenemedi");
                setReportData(null);
            } finally {
                setLoading(false);
                setIsTableLoading(false);
            }
        },
        [selectedDepartments, limit]
    );

    useEffect(() => {
        if (filtered && selectedDepartments.length > 0) {
            loadReport(1);
        }
    }, [filtered, selectedDepartments]);

    const handleFilter = () => {
        if (selectedDepartments.length > 0) {
            setFiltered(true);
            setPage(1);
            loadReport(1);
        }
    };

    const handleClearFilters = () => {
        setSelectedDepartments([]);
        setReportData(null);
        setFiltered(false);
        setPage(1);
        setError(null);
    };

    const handleDepartmentChange = (value: string[]) => {
        setSelectedDepartments(value);
        setFiltered(false);
        setReportData(null);
        setError(null);
    };

    const handleRefresh = () => {
        if (selectedDepartments.length > 0) {
            loadReport(page);
        }
    };

    useEffect(() => {
        if (!selectedDepartments || selectedDepartments.length === 0) {
            setAvailablePeriods([]);
            setSelectedPeriods([]);
            setMonthlyReportData(null);

            return;
        };

        const loadPeriods = async () => {
            setLoadingPeriods(true);

            try {
                const response = await ApiService.getAvailableDepartmentPeriods(selectedDepartments[0]);

                const periods = Array.isArray(response) ? response : ((response as any)?.periods || []);
                setAvailablePeriods(periods);
            } catch (err: any) {
                console.error("Error loading periods:", err);
                setAvailablePeriods([]);
            } finally {
                setLoadingPeriods(false);
            }
        };

        loadPeriods();
    }, [selectedDepartments]);

    const loadMonthlyReport = useCallback(async (currentPage: number = 1, isPagination: boolean = false) => {
        if (!selectedDepartments || selectedDepartments.length === 0 || selectedPeriods.length === 0) return;

        if (isPagination) setIsTableLoading(true); else setMonthlyLoading(true);
        setError(null);

        try {
            const response = await ApiService.getDepartmentMultiMonthReport(
                selectedDepartments[0],
                selectedPeriods,
                { page: currentPage, limit: 20, sortBy: 'avgSuccessRate', sortOrder: 'DESC' }
            );

            setMonthlyReportData(response);
        } catch (err: any) {
            console.error("Error loading monthly report:", err);

            setError(err?.message || 'Aylık rapor yüklenemedi');
            setMonthlyReportData(null);
        } finally {
            setMonthlyLoading(false);
            setIsTableLoading(false);
        }
    }, [selectedDepartments, selectedPeriods]);

    const handleViewDetail = async (userId: string) => {
        setDetailModalOpen(true);
        setDetailLoading(true);
        setDetailData(null);
        setDetailError(null);
        setExpandedLessons(new Set());

        try {
            const data = await ApiService.getUserDetailScores(userId);

            if (!data || !data.user) {
                throw new Error("API geçersiz veri döndürdü. Backend'i yeniden başlatmayı deneyin.");
            }

            setDetailData(data);
        } catch (err: any) {
            console.error("Error loading user detail scores:", err);
            setDetailError(err?.message || "Öğrenci detayları yüklenirken bir hata oluştu");
            setDetailData(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const toggleLessonExpand = (lessonId: string) => {
        setExpandedLessons((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(lessonId)) {
                newSet.delete(lessonId);
            } else {
                newSet.add(lessonId);
            }
            return newSet;
        });
    };

    const getPerformanceLevel = (score: number) => {
        if (score >= 90)
            return {
                text: "Mükemmel",
                color: "text-yellow-600",
                bgColor: "bg-yellow-50",
                borderColor: "border-yellow-200",
                Icon: Trophy,
            };
        if (score >= 80)
            return {
                text: "Çok İyi",
                color: "text-green-600",
                bgColor: "bg-green-50",
                borderColor: "border-green-200",
                Icon: Award,
            };
        if (score >= 70)
            return {
                text: "İyi",
                color: "text-blue-600",
                bgColor: "bg-blue-50",
                borderColor: "border-blue-200",
                Icon: TrendingUp,
            };
        if (score >= 50)
            return {
                text: "Orta",
                color: "text-orange-600",
                bgColor: "bg-orange-50",
                borderColor: "border-orange-200",
                Icon: Target,
            };
        return {
            text: "Geliştirilmeli",
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            Icon: AlertCircle,
        };
    };

    const getScoreColor = (score: number) => {
        if (score >= 90) return "text-yellow-600";
        if (score >= 80) return "text-green-600";
        if (score >= 70) return "text-blue-600";
        if (score >= 50) return "text-orange-600";
        return "text-red-600";
    };

    const getScoreBgColor = (score: number) => {
        if (score >= 90) return "bg-yellow-100";
        if (score >= 80) return "bg-green-100";
        if (score >= 70) return "bg-blue-100";
        if (score >= 50) return "bg-orange-100";
        return "bg-red-100";
    };

    const renderScoreBar = (score: number, className?: string) => {
        const bgColor =
            score >= 90
                ? "bg-yellow-500"
                : score >= 80
                    ? "bg-green-500"
                    : score >= 70
                        ? "bg-blue-500"
                        : score >= 50
                            ? "bg-orange-500"
                            : "bg-red-500";

        return (
            <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
                <div
                    className={`h-2 rounded-full transition-all duration-300 ${bgColor}`}
                    style={{ width: `${Math.min(score, 100)}%` }}
                />
            </div>
        );
    };

    const isFilterReady = () => {
        return selectedDepartments.length > 0;
    };

    const renderFilterArea = () => (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filtreler</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 sm:mb-6">
                <div>
                    <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        <GraduationCap className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        Şube Seçimi
                    </label>
                    {settingsLoading ? (
                        <div className="border border-gray-300 rounded-lg p-3 flex items-center gap-2 text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Yükleniyor...</span>
                        </div>
                    ) : (
                        <DepartmentTreeSelect
                            value={selectedDepartments}
                            onChange={handleDepartmentChange}
                            placeholder="Şube seçin..."
                            multiple={false}
                            rootId={studentDepartmentId}
                            onlyLeaves={true}
                        />
                    )}
                </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <button
                    onClick={handleFilter}
                    disabled={!isFilterReady()}
                    className={`w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 rounded-lg font-medium text-sm transition-colors ${isFilterReady()
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                >
                    Rapor Oluştur
                </button>
                {!isFilterReady() && (
                    <span className="text-xs text-gray-600">
                        Başarı puanı raporunu görüntülemek için lütfen şube seçiniz.
                    </span>
                )}
            </div>
        </div>
    );

    const renderStatisticsCards = () => {
        if (!reportData) return null;

        const performance = getPerformanceLevel(reportData.departmentScore);
        const PerformanceIcon = performance.Icon;

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className={`rounded-lg border ${performance.borderColor} ${performance.bgColor} p-4`}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Genel Başarı Puanı</span>
                        <PerformanceIcon className={`w-5 h-5 ${performance.color}`} />
                    </div>
                    <div className={`text-3xl font-bold ${performance.color}`}>
                        %{reportData.departmentScore}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{performance.text}</div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Toplam Öğrenci</span>
                        <User className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                        {reportData.statistics.totalStudents}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Kayıtlı öğrenci</div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Toplam Ödev</span>
                        <BookOpen className="w-5 h-5 text-purple-500" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                        {reportData.statistics.totalHomeworks}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        {reportData.statistics.completedHomeworks} tamamlandı
                    </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Tamamlanma Oranı</span>
                        <BarChart3 className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                        %{reportData.statistics.overallCompletionRate}
                    </div>
                    {renderScoreBar(reportData.statistics.overallCompletionRate, "mt-2")}
                </div>
            </div>
        );
    };

    const renderLessonScores = () => {
        if (!reportData || reportData.lessonScores.length === 0) return null;

        return (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    Ders Bazlı Başarı Puanları
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {reportData.lessonScores.map((lesson) => (
                        <div
                            key={lesson.id}
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900 truncate flex-1 mr-2">
                                    {lesson.title}
                                </span>
                                <span className={`text-lg font-bold ${getScoreColor(lesson.successRate)}`}>
                                    %{lesson.successRate}
                                </span>
                            </div>
                            {renderScoreBar(lesson.successRate)}
                            <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                                <span>{lesson.completedHomeworks}/{lesson.totalHomeworks} tamamlandı</span>
                                <span>%{lesson.completionRate} tamamlanma</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderUserScores = () => {
        if (!reportData || reportData.data.length === 0) {
            return (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Bu şube için öğrenci verisi bulunamadı.</p>
                </div>
            );
        }

        const desktopGridClass = "hidden md:grid md:grid-cols-[60px_minmax(200px,1fr)_100px_160px_130px_160px_100px] items-center gap-4";

        return (
            <div className="mt-4 sm:mt-8 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden relative">

                {isTableLoading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 backdrop-blur-sm transition-all duration-300">
                        <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                            <span className="text-sm font-medium text-gray-600">Yükleniyor...</span>
                        </div>
                    </div>
                )}

                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        Öğrenci Başarı Puanları
                    </h3>

                    {reportData.meta.totalPages > 1 && (
                        <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
                            <span className="text-xs sm:text-sm text-gray-500 font-medium">
                                {(reportData.meta.currentPage - 1) * limit + 1} - {Math.min(reportData.meta.currentPage * limit, reportData.meta.totalItems)} / {reportData.meta.totalItems}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => !isTableLoading && loadReport(page - 1, true)}
                                    disabled={page <= 1 || isTableLoading}
                                    className="p-1.5 sm:p-2 rounded-md border border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => !isTableLoading && loadReport(page + 1, true)}
                                    disabled={page >= reportData.meta.totalPages || isTableLoading}
                                    className="p-1.5 sm:p-2 rounded-md border border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className={`bg-gray-50/80 px-4 py-3 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider ${desktopGridClass}`}>
                    <div className="text-center">Sıra</div>
                    <div className="pl-11">Öğrenci</div>
                    <div className="text-center">Şube</div>
                    <div className="text-center">Başarı Puanı</div>
                    <div className="text-center">Tamamlanan</div>
                    <div className="text-center">Tamamlanma</div>
                    <div className="text-center">İşlemler</div>
                </div>

                <div className="divide-y divide-gray-100">
                    {reportData.data.map((item: UserScore, index: number) => {
                        const performance = getPerformanceLevel(item.successRate);
                        const rank = (page - 1) * limit + index + 1;

                        return (
                            <div key={item.user.id}>

                                <div className={`${desktopGridClass} p-4 hover:bg-gray-50 transition-colors`}>
                                    <div className="flex justify-center">
                                        <span
                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${rank <= 3
                                                ? rank === 1
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : rank === 2
                                                        ? "bg-gray-100 text-gray-700"
                                                        : "bg-orange-100 text-orange-700"
                                                : "bg-gray-50 text-gray-500"
                                                }`}
                                        >
                                            {rank}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="flex-shrink-0 h-10 w-10">
                                            {item.user.avatar ? (
                                                <img
                                                    className="h-10 w-10 rounded-full object-cover"
                                                    src={item.user.avatar}
                                                    alt=""
                                                />
                                            ) : (
                                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <User className="h-5 w-5 text-blue-600" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                {item.user.firstName} {item.user.lastName}
                                            </div>
                                            {item.user.schoolNumber && (
                                                <div className="text-xs text-gray-500 truncate">
                                                    No: {item.user.schoolNumber}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-center">
                                        {item.user.department ? (
                                            <span
                                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-white shadow-sm"
                                                style={{ backgroundColor: item.user.department.color }}
                                            >
                                                {item.user.department.name}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <span
                                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold shadow-sm ${getScoreBgColor(
                                                item.successRate
                                            )} ${getScoreColor(item.successRate)}`}
                                        >
                                            %{item.successRate}
                                        </span>
                                        <span className={`text-xs mt-1 font-medium ${performance.color}`}>
                                            {performance.text}
                                        </span>
                                    </div>

                                    <div className="flex flex-col items-center">
                                        <div className="text-sm font-medium text-gray-900">
                                            {item.completedHomeworks}/{item.totalHomeworks}
                                        </div>
                                        <div className="text-xs text-gray-500">Tamamlanan</div>
                                    </div>

                                    <div className="flex flex-col items-center w-full">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-medium text-gray-900">%{item.completionRate}</span>
                                        </div>
                                        <div className="w-full max-w-[100px]">
                                            {renderScoreBar(item.completionRate)}
                                        </div>
                                    </div>

                                    <div className="flex justify-center">
                                        <button
                                            onClick={() => handleViewDetail(item.user.id)}
                                            className="inline-flex items-center justify-center w-9 h-9 border border-blue-200 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
                                            title="Detay Görüntüle"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="md:hidden p-4 bg-white">
                                    <div className="flex items-center gap-3 mb-3">

                                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${rank <= 3
                                            ? rank === 1 ? "bg-yellow-100 text-yellow-700"
                                                : rank === 2 ? "bg-gray-200 text-gray-700"
                                                    : "bg-orange-100 text-orange-700"
                                            : "bg-gray-100 text-gray-500"
                                            }`}>
                                            {rank}
                                        </span>

                                        <div className="flex-shrink-0">
                                            {item.user.avatar ? (
                                                <img className="h-11 w-11 rounded-full object-cover" src={item.user.avatar} alt="" />
                                            ) : (
                                                <div className="h-11 w-11 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <User className="h-5 w-5 text-blue-600" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                {item.user.firstName} {item.user.lastName}
                                            </p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                {item.user.schoolNumber && (
                                                    <span className="text-xs text-gray-500">No: {item.user.schoolNumber}</span>
                                                )}
                                                {item.user.department && (
                                                    <span
                                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                                                        style={{ backgroundColor: item.user.department.color }}
                                                    >
                                                        {item.user.department.name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleViewDetail(item.user.id)}
                                            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 border border-blue-200 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">

                                        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                                            <p className="text-[10px] text-gray-500 mb-1">Başarı</p>
                                            <p className={`text-base font-bold ${getScoreColor(item.successRate)}`}>
                                                %{item.successRate}
                                            </p>
                                            <p className={`text-[10px] mt-0.5 ${performance.color}`}>
                                                {performance.text}
                                            </p>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                                            <p className="text-[10px] text-gray-500 mb-1">Tamamlanan</p>
                                            <p className="text-base font-bold text-gray-900">
                                                {item.completedHomeworks}<span className="text-gray-400 text-xs">/{item.totalHomeworks}</span>
                                            </p>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                                            <p className="text-[10px] text-gray-500 mb-1">Tamamlanma</p>
                                            <p className="text-base font-bold text-blue-600">%{item.completionRate}</p>
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-gray-500">Tamamlanma Durumu</span>
                                            <span className="font-medium text-gray-700">%{item.completionRate}</span>
                                        </div>
                                        {renderScoreBar(item.completionRate, "h-2")}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderDetailModal = () => {
        if (!detailModalOpen) return null;

        return (
            <Modal isOpen={detailModalOpen} onClose={() => setDetailModalOpen(false)} size="xl" title="Öğrenci Detay Raporu">
                {detailLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">Yükleniyor...</span>
                    </div>
                ) : detailData && detailData.user ? (
                    <div className="max-h-[70vh] overflow-y-auto">

                        <div className="flex flex-col sm:flex-row items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                            <div className="flex-shrink-0 h-16 w-16">
                                {detailData.user.avatar ? (
                                    <img
                                        className="h-16 w-16 rounded-full object-cover"
                                        src={detailData.user.avatar}
                                        alt=""
                                    />
                                ) : (
                                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                                        <User className="h-8 w-8 text-blue-600" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {detailData.user.firstName} {detailData.user.lastName}
                                </h3>
                                {detailData.user.schoolNumber && (
                                    <p className="text-sm text-gray-500">Okul No: {detailData.user.schoolNumber}</p>
                                )}
                                {detailData.user.department && (
                                    <span
                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white mt-1"
                                        style={{ backgroundColor: detailData.user.department.color }}
                                    >
                                        {detailData.user.department.name}
                                    </span>
                                )}
                            </div>
                            <div className="text-center sm:text-right w-full sm:w-auto border-t sm:border-0 pt-2 sm:pt-0 border-gray-200">
                                <div
                                    className={`text-3xl font-bold ${getScoreColor(detailData.successRate)}`}
                                >
                                    %{detailData.successRate}
                                </div>
                                <div className="text-sm text-gray-500">Genel Başarı Puanı</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">
                                    {detailData.totalHomeworks}
                                </div>
                                <div className="text-xs text-gray-500">Toplam Ödev</div>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">
                                    {detailData.completedHomeworks}
                                </div>
                                <div className="text-xs text-gray-500">Tamamlanan</div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600">
                                    %{detailData.completionRate}
                                </div>
                                <div className="text-xs text-gray-500">Tamamlanma</div>
                            </div>
                        </div>

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                <h4 className="font-medium text-gray-900">Ders Bazlı Başarı Puanları</h4>
                            </div>
                            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                                {detailData.lessonScores.length === 0 ? (
                                    <div className="p-4 text-center text-gray-500">
                                        Henüz ders verisi bulunmamaktadır.
                                    </div>
                                ) : (
                                    detailData.lessonScores.map((lesson) => (
                                        <div key={lesson.id}>
                                            <div
                                                className="p-4 hover:bg-gray-50 cursor-pointer flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                                                onClick={() => toggleLessonExpand(lesson.id)}
                                            >
                                                <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
                                                    <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-gray-900 truncate">{lesson.title}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {lesson.completedHomeworks}/{lesson.totalHomeworks} tamamlandı
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                                                    <div className="text-right flex items-center gap-3 sm:block">
                                                        <span
                                                            className={`text-lg font-bold ${getScoreColor(
                                                                lesson.successRate
                                                            )}`}
                                                        >
                                                            %{lesson.successRate}
                                                        </span>
                                                        <div className="w-24">{renderScoreBar(lesson.successRate)}</div>
                                                    </div>
                                                    {lesson.topics && lesson.topics.length > 0 && (
                                                        expandedLessons.has(lesson.id) ? (
                                                            <ChevronUp className="w-5 h-5 text-gray-400" />
                                                        ) : (
                                                            <ChevronDown className="w-5 h-5 text-gray-400" />
                                                        )
                                                    )}
                                                </div>
                                            </div>

                                            {expandedLessons.has(lesson.id) &&
                                                lesson.topics &&
                                                lesson.topics.length > 0 && (
                                                    <div className="bg-gray-50 border-t border-gray-200">
                                                        {lesson.topics.map((topic) => (
                                                            <div
                                                                key={topic.id}
                                                                className="px-4 py-3 pl-4 sm:pl-12 flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-gray-100 last:border-b-0 gap-2"
                                                            >
                                                                <div className="flex-1 w-full sm:w-auto">
                                                                    <div className="text-sm text-gray-700">{topic.title}</div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {topic.completedHomeworks}/{topic.totalHomeworks} tamamlandı
                                                                    </div>
                                                                </div>
                                                                <div className="text-right w-full sm:w-auto flex items-center justify-between sm:block">
                                                                    <span
                                                                        className={`text-sm font-bold ${getScoreColor(
                                                                            topic.successRate
                                                                        )}`}
                                                                    >
                                                                        %{topic.successRate}
                                                                    </span>
                                                                    <div className="w-16">{renderScoreBar(topic.successRate)}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-red-300 mb-4" />
                        <p className="text-gray-500">{detailError || "Öğrenci detayları yüklenirken bir hata oluştu."}</p>
                    </div>
                )}
            </Modal>
        );
    };

    const renderMonthlyFilterArea = () => (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
                <Filter className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Aylık Rapor Filtreleri</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <GraduationCap className="w-4 h-4" />
                        Şube Seçimi
                    </label>
                    {settingsLoading ? (
                        <div className="border border-gray-300 rounded-lg p-3 flex items-center gap-2 text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Yükleniyor...</span>
                        </div>
                    ) : (
                        <DepartmentTreeSelect
                            value={selectedDepartments}
                            onChange={handleDepartmentChange}
                            placeholder="Şube seçin..."
                            multiple={false}
                            rootId={studentDepartmentId}
                            onlyLeaves={true}
                        />
                    )}
                </div>

                <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="w-4 h-4" />
                        Ay Seçimi {selectedPeriods.length > 0 && `(${selectedPeriods.length} Seçili)`}
                    </label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                        <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                            {loadingPeriods ? (
                                <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Yükleniyor...
                                </div>
                            ) : !selectedDepartments || selectedDepartments.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-500">Dönem yok.</div>
                            ) : availablePeriods.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-500">Dönem yok.</div>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedPeriods([])}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedPeriods.length === 0 ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-600"}`}
                                        disabled={!selectedDepartments || selectedDepartments.length === 0}>
                                        Dönem Seçiniz
                                    </button>
                                    {availablePeriods.map((period) => {
                                        const isSelected = selectedPeriods.includes(period.period);
                                        return (
                                            <button
                                                key={period.period}
                                                type="button"
                                                onClick={() => {
                                                    const periodSet = new Set(selectedPeriods);
                                                    if (periodSet.has(period.period)) {
                                                        periodSet.delete(period.period);
                                                    } else {
                                                        periodSet.add(period.period);
                                                    }
                                                    setSelectedPeriods(Array.from(periodSet).sort());
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${isSelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"}`}
                                                disabled={!selectedDepartments || selectedDepartments.length === 0}>
                                                <span className="font-medium">{period.name}</span>
                                                {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                                            </button>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex gap-2">
                <button
                    onClick={() => { setMonthlyFiltered(true); loadMonthlyReport(1); }}
                    disabled={!selectedDepartments || selectedDepartments.length === 0 || selectedPeriods.length === 0}
                    className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors ${selectedDepartments && selectedDepartments.length > 0 && selectedPeriods.length > 0
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                >
                    Raporu Göster
                </button>
                {monthlyFiltered && (
                    <button
                        onClick={() => { setMonthlyFiltered(false); setMonthlyReportData(null); }}
                        className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Temizle
                    </button>
                )}
            </div>
        </div>
    );

    const renderMonthlyReportTable = () => {
        if (!monthlyReportData?.data) return null;

        const periods = monthlyReportData.periods || [];
        const periodData = monthlyReportData.periodData || {};

        let totalAvgSuccessRate = 0;
        let periodCountWithStats = 0;
        for (const period of periods) {
            const stats = periodData[period.period];

            if (stats && stats.avgSuccessRate > 0) {
                totalAvgSuccessRate += stats.avgSuccessRate;
                periodCountWithStats++;
            };
        }

        const overallAvgSuccessRate = periodCountWithStats > 0 ? totalAvgSuccessRate / periodCountWithStats : 0;
        const performance = getPerformanceLevel(overallAvgSuccessRate);
        const PerformanceIcon = performance.Icon;

        return (
            <>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className={`rounded-lg border ${performance.borderColor} ${performance.bgColor} p-4`}>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">Genel Başarı Puanı</span>
                            <PerformanceIcon className={`w-5 h-5 ${performance.color}`} />
                        </div>
                        <div className={`text-3xl font-bold ${performance.color}`}>
                            %{overallAvgSuccessRate.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{performance.text}</div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">Toplam Öğrenci</span>
                            <User className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {monthlyReportData.statistics.totalStudents}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Kayıtlı öğrenci</div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">Toplam Ödev</span>
                            <BookOpen className="w-5 h-5 text-purple-500" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {monthlyReportData.statistics.totalHomeworks}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {monthlyReportData.statistics.completedHomeworks} tamamlandı
                        </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">Tamamlanma Oranı</span>
                            <BarChart3 className="w-5 h-5 text-green-500" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            %{monthlyReportData.statistics.overallCompletionRate.toFixed(1)}
                        </div>
                        {renderScoreBar(monthlyReportData.statistics.overallCompletionRate, "mt-2")}
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">

                    {periods.length > 1 && (
                        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50 first:rounded-t-lg">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 sm:mb-4">Dönem Bazlı İstatistikler</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                                {periods.map((period: any) => {
                                    const stats = periodData[period.period] || {};
                                    return (
                                        <div key={period.period} className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
                                            <p className="text-sm font-medium text-gray-700 mb-2">{period.name}</p>
                                            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                                                <div>
                                                    <p className="text-gray-500">Başarı</p>
                                                    <p className={`font-bold ${getScoreColor(stats.avgSuccessRate || 0)}`}>
                                                        {(stats.avgSuccessRate || 0).toFixed(1)}%
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-500">Tamamlanma</p>
                                                    <p className="font-bold text-blue-600">{(stats.completionRate || 0).toFixed(1)}%</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-500">Öğrenci</p>
                                                    <p className="font-bold text-gray-900">{stats.studentCount || 0}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-500">Ödev</p>
                                                    <p className="font-bold text-gray-900">{stats.totalHomeworks || 0}</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="block md:hidden divide-y divide-gray-200">
                        {monthlyReportData.data.map((item: any) => (
                            <div key={item.userId} className="p-4 space-y-3">

                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 h-10 w-10">
                                        {item.user?.avatar ? (
                                            <img className="h-10 w-10 rounded-full object-cover" src={item.user.avatar} alt="" />
                                        ) : (
                                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                <User className="h-5 w-5 text-blue-600" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 truncate">
                                            {item.user?.firstName} {item.user?.lastName}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">{item.user?.email || ''}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <p className="text-xs text-gray-500 mb-1">Ort. Başarı</p>
                                        <p className={`text-lg font-bold ${getScoreColor(item.avgSuccessRate)}`}>
                                            {item.avgSuccessRate.toFixed(1)}%
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                                        <p className="text-xs text-gray-500 mb-1">Ort. Tamamlanma</p>
                                        <p className="text-lg font-bold text-blue-600">{item.avgCompletionRate.toFixed(1)}%</p>
                                    </div>
                                </div>

                                {periods.map((period: any) => {
                                    const pData = item.periodData?.[period.period];
                                    const successRate = pData?.successRate || 0;
                                    const completionRate = pData?.completionRate || 0;
                                    const hasData = pData && pData.totalHomeworks > 0;

                                    return (
                                        <div key={`${item.userId}-${period.period}`} className="bg-gray-50 rounded-lg p-3">
                                            <p className="text-xs font-semibold text-gray-600 mb-2">{period.name}</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <p className="text-xs text-gray-500">Başarı</p>
                                                    {hasData ? (
                                                        <p className={`font-bold ${getScoreColor(successRate)}`}>
                                                            {successRate.toFixed(1)}%
                                                        </p>
                                                    ) : (
                                                        <p className="text-gray-400 text-sm">-</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Tamamlanma</p>
                                                    {hasData ? (
                                                        <p className="font-bold text-blue-600">{completionRate.toFixed(1)}%</p>
                                                    ) : (
                                                        <p className="text-gray-400 text-sm">-</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">Öğrenci</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ort. Başarı</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ort. Tamamlanma</th>
                                    {periods.map((period: any) => (
                                        <th key={period.period} colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-300">
                                            {period.name}
                                        </th>
                                    ))}
                                </tr>
                                <tr className="bg-gray-100">
                                    <th className="px-4 py-2"></th>
                                    <th className="px-4 py-2"></th>
                                    <th className="px-4 py-2"></th>
                                    {periods.map((period: any) => (
                                        <React.Fragment key={`sub-${period.period}`}>
                                            <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Başarı %</th>
                                            <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Tamamlanma</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {monthlyReportData.data.map((item: any) => (
                                    <tr key={item.userId} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-4 sticky left-0 bg-white">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-shrink-0 h-8 w-8">
                                                    {item.user?.avatar ? (
                                                        <img className="h-8 w-8 rounded-full object-cover" src={item.user.avatar} alt="" />
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                                            <User className="h-4 w-4 text-blue-600" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 text-sm">
                                                        {item.user?.firstName} {item.user?.lastName}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{item.user?.email || ''}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className={`font-bold ${getScoreColor(item.avgSuccessRate)}`}>
                                                {item.avgSuccessRate.toFixed(1)}%
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className="font-medium text-blue-600">{item.avgCompletionRate.toFixed(1)}%</span>
                                        </td>
                                        {periods.map((period: any) => {
                                            const pData = item.periodData?.[period.period];
                                            const successRate = pData?.successRate || 0;
                                            const completionRate = pData?.completionRate || 0;
                                            const hasData = pData && pData.totalHomeworks > 0;

                                            return (
                                                <React.Fragment key={`${item.userId}-${period.period}`}>
                                                    <td className="px-2 py-4 text-center border-l border-gray-300">
                                                        {hasData ? (
                                                            <span className={`font-semibold ${getScoreColor(successRate)}`}>
                                                                {successRate.toFixed(1)}%
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-4 text-center">
                                                        {hasData ? (
                                                            <span className="font-medium text-blue-600">{completionRate.toFixed(1)}%</span>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">-</span>
                                                        )}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {monthlyReportData.meta.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center px-4 sm:px-6 py-4 border-t border-gray-200 gap-3">
                            <span className="text-sm text-gray-700 order-2 sm:order-1">
                                {monthlyReportData.meta.totalItems} öğrenci
                            </span>
                            <div className="flex items-center gap-2 order-1 sm:order-2">
                                <button
                                    onClick={() => loadMonthlyReport(monthlyReportData.meta.currentPage - 1, true)}
                                    disabled={monthlyReportData.meta.currentPage === 1}
                                    className="p-2 sm:px-3 sm:py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4 sm:hidden" />
                                    <span className="hidden sm:inline">Önceki</span>
                                </button>
                                <span className="px-3 py-1 text-sm text-gray-700 font-medium">
                                    {monthlyReportData.meta.currentPage} / {monthlyReportData.meta.totalPages}
                                </span>
                                <button
                                    onClick={() => loadMonthlyReport(monthlyReportData.meta.currentPage + 1, true)}
                                    disabled={monthlyReportData.meta.currentPage === monthlyReportData.meta.totalPages}
                                    className="p-2 sm:px-3 sm:py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                >
                                    <ChevronRight className="w-4 h-4 sm:hidden" />
                                    <span className="hidden sm:inline">Sonraki</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div className="h-full flex flex-col">

            {mode === "instant" && (
                <>
                    {!filtered && renderFilterArea()}

                    {filtered && (
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex flex-row flex-wrap gap-2">
                                <button
                                    onClick={handleClearFilters}
                                    className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                                >
                                    <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    <span>Filtrele</span>
                                </button>
                                <button
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300 disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loading ? "animate-spin" : ""}`} />
                                    <span>Yenile</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                            <span className="text-red-700">{error}</span>
                        </div>
                    )}

                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            <span className="ml-2 text-gray-600">Rapor yükleniyor...</span>
                        </div>
                    )}

                    {filtered && !loading && reportData && (
                        <div className="flex-1 min-h-0 overflow-y-auto">
                            {renderStatisticsCards()}
                            {renderLessonScores()}
                            {renderUserScores()}
                        </div>
                    )}
                </>
            )}

            {mode === "monthly" && (
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {!monthlyFiltered && renderMonthlyFilterArea()}

                    {monthlyFiltered && (
                        <div className="mb-6">
                            <button
                                onClick={() => { setMonthlyFiltered(false); setMonthlyReportData(null); }}
                                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Filtreleri Değiştir
                            </button>
                        </div>
                    )}

                    {monthlyLoading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            <span className="ml-2 text-gray-700">Aylık rapor yükleniyor...</span>
                        </div>
                    )}

                    {error && monthlyFiltered && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                                <span className="text-red-700">{error}</span>
                            </div>
                        </div>
                    )}

                    {monthlyFiltered && !monthlyLoading && monthlyReportData && renderMonthlyReportTable()}
                </div>
            )}

            {renderDetailModal()}
        </div>
    );
}